import Checkbox from "@app/components/Checkbox";
import Loading from "@app/components/Loading";
import { orderSort } from "@app/lib/utils";
import { themesAtom } from "@app/store/themeStore";
import { MultiSelect } from "@design-system/Inputs/MultiSelect";

import { FlexRow } from "@design-system/Layout/Flex";
import { HeaderFilter } from "@design-system/Table";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import { Text } from "@design-system/Typography/Text";
import { cn } from "@design-system/Utilities";
import { useAtom } from "jotai";
import { type FC, type PropsWithChildren, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./i18n";

const SubthemeMultiSelect: FC<
    PropsWithChildren<{
        isHeader?: boolean;
        values: (string | null)[] | undefined;
        onValuesChange: (val: (string | null)[]) => void;
        searchFilter?: string | undefined;
        onSearchFilterChange?: (val: string | undefined) => void;
        hasPortal?: boolean;
        isModal?: boolean;
    }>
> = ({
    isHeader,
    hasPortal = false,
    values,
    onValuesChange,
    searchFilter,
    onSearchFilterChange,
    isModal,
}) => {
    const [{ data, isLoading }] = useAtom(themesAtom);
    const { t } = useTranslation("SubthemeMultiSelect");

    const filteredthemes = useMemo(() => {
        return orderSort(data?.themes || [])
            .map((theme) => {
                const isThemeInSearch =
                    !searchFilter ||
                    theme.name
                        .toLowerCase()
                        .includes(searchFilter.toLowerCase());
                if (isThemeInSearch) {
                    const filteredSubThemesIds = theme.subthemes.map(
                        (elt) => elt.id,
                    );
                    return { ...theme, filteredSubThemesIds };
                }
                const filteredSubThemesIds = theme.subthemes
                    .filter(
                        ({ name }) =>
                            !searchFilter ||
                            name
                                .toLowerCase()
                                .includes(searchFilter.toLowerCase()),
                    )
                    .map((elt) => elt.id);

                return { ...theme, filteredSubThemesIds: filteredSubThemesIds };
            })
            .filter((theme) => {
                return (
                    theme.subthemes.filter((elt) =>
                        theme.filteredSubThemesIds?.includes(elt.id),
                    ).length > 0
                );
            })
            .map((theme) => {
                const subthemes = orderSort(theme.subthemes).filter((elt) => {
                    return theme.filteredSubThemesIds?.includes(elt.id);
                });
                return {
                    ...theme,
                    subthemes: subthemes,
                    subThemeIds: subthemes.map((elt) => elt.id),
                };
            });
    }, [data, searchFilter]);

    const active = !!values?.length;

    return (
        <MultiSelect
            values={values || []}
            onValuesChange={onValuesChange}
            modal={isModal}
        >
            {isHeader ? (
                <MultiSelect.Trigger>
                    <HeaderFilter active={active}>
                        {t("placeholder", {
                            context: active && "active",
                            count: values?.length,
                        })}
                    </HeaderFilter>
                </MultiSelect.Trigger>
            ) : (
                <MultiSelect.ChipTrigger
                    label={t("placeholder")}
                    options={filteredthemes.flatMap((theme) => theme.subthemes)}
                    valueIdentifierKey="id"
                    valueDisplayKey="name"
                    className="w-fit"
                />
            )}

            <MultiSelect.Content hasPortal={hasPortal}>
                {isLoading && <Loading size="small" />}
                {!isLoading && onSearchFilterChange && (
                    <MultiSelect.Search
                        searchString={searchFilter || ""}
                        setSearchString={onSearchFilterChange}
                        placeholder={t("searchbar_placeholder")}
                    ></MultiSelect.Search>
                )}
                <MultiSelect.Reset label={t("reset_label")} />
                {!isLoading &&
                    filteredthemes.map((theme, idx) => (
                        <MultiSelect.Group key={idx}>
                            <>
                                <MultiSelect.Label
                                    value={theme.id || null}
                                    subItemsValues={theme.subThemeIds}
                                >
                                    {({ isGroupSelected }) => (
                                        <FlexRow
                                            alignItems="center"
                                            alignSelf="stretch"
                                            className={cn("flex-1")}
                                            gap="3"
                                        >
                                            <Checkbox
                                                checked={isGroupSelected}
                                                color={theme.color}
                                            />
                                            <Text>
                                                <Ellipsis asChild>
                                                    <span>{theme.name}</span>
                                                </Ellipsis>
                                            </Text>
                                        </FlexRow>
                                    )}
                                </MultiSelect.Label>

                                {theme.subthemes.map((subtheme, idx2) => (
                                    <MultiSelect.Item
                                        key={idx2}
                                        value={subtheme.id}
                                    >
                                        {({ isSelected }) => (
                                            <FlexRow
                                                alignItems="center"
                                                alignSelf="stretch"
                                                gap="3"
                                                className={cn(
                                                    " ml-[20px] flex-1",
                                                )}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    color={theme.color}
                                                />
                                                <Text>
                                                    <Ellipsis
                                                        asChild
                                                        className="whitespace-nowrap"
                                                    >
                                                        <span>
                                                            {subtheme.name}
                                                        </span>
                                                    </Ellipsis>
                                                </Text>
                                            </FlexRow>
                                        )}
                                    </MultiSelect.Item>
                                ))}
                            </>
                        </MultiSelect.Group>
                    ))}
            </MultiSelect.Content>
        </MultiSelect>
    );
};

export default SubthemeMultiSelect;
